import { promiseState } from '@passionware/platform-react';
import { rd } from '@passionware/monads';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Callout } from '__types__/v1/models/Callout';
import { Form } from 'v5/design-system/Form';
import { CalloutDetailsFields } from 'v5/features/callouts/_shared/CalloutDetailsFields';
import { appScopeUtils } from 'v5/core/app-scope/appScope.types';
import { CalloutEditFormData } from 'v5/features/callouts/callout-create.types';
import {
  isValidCalloutSlug,
  normalizeCalloutSlugForSubmit
} from 'v5/api/callouts/callout-slug.logic';
import { AsyncButton } from 'v5/patterns/forms/fields/AsyncButton';
import { DialogErrorFooter } from 'v5/patterns/forms/dialogs/DialogErrorFooter';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { useRegisterDirtyState } from 'v5/patterns/forms/unsaved-changes/useRegisterDirtyState';
import { WithServices } from 'v5/platform/ts/services';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithCalloutsService } from 'v5/services/io/CalloutsService/CalloutsService';

export interface EditCalloutDialogContentProps
  extends WithServices<
    [
      WithResourceTypeService,
      WithCalloutsService,
      WithAppScopeService,
      WithEnterpriseService
    ]
  > {
  callout: Callout;
  onSubmit?: (data: CalloutEditFormData) => Promise<void> | void;
  open?: boolean;
}

export function EditCalloutDialogContent({
  callout,
  onSubmit,
  open = true,
  services
}: EditCalloutDialogContentProps) {
  const { t } = useTranslation('callouts');

  const form = useForm<CalloutEditFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      title: callout.title ?? '',
      slug: callout.slug ?? '',
      resource_type_id: callout.resource_type_id ?? 0
    }
  });

  const mutation = promiseState.useMutation(
    async (data: CalloutEditFormData) => {
      const slug = normalizeCalloutSlugForSubmit(data.slug);
      if (!isValidCalloutSlug(slug)) {
        throw new Error(t('CalloutDialog.slugInvalid'));
      }
      const resolvedSlug =
        slug === callout.slug
          ? slug
          : await services.calloutsService.resolveUniqueSlug(slug, {
              excludeCalloutId: callout.id,
              workspaceId: callout.enterprise_workspace_id ?? null
            });
      return onSubmit?.({
        ...data,
        slug: resolvedSlug
      });
    }
  );

  useRegisterDirtyState(form.formState.isDirty);

  const scopeRd = services.appScopeService.useAppScope();
  const resourceTypes = services.resourceTypeService.useResourceTypes(
    rd.tryMap(scopeRd, scope =>
      appScopeUtils.identityFromEntityWorkspace(
        scope.organizationId,
        callout.enterprise_workspace_id
      )
    )
  );

  return (
    <Form {...form}>
      <FormDialogContent
        onSubmit={form.handleSubmit(data => mutation.track(data))}
        open={open}
        title={t('CalloutDialog.editCallout')}
        saveText={t('CalloutDialog.saveChanges')}
        mutation={mutation.state}
        className="max-w-150"
        footerContent={<DialogErrorFooter mutation={mutation.state} />}
        saveButtonSlot={
          <AsyncButton
            type="submit"
            mutation={mutation.state}
            variant="primary"
            visuallyDisabled={
              !form.formState.isDirty || !form.formState.isValid
            }
          >
            {t('CalloutDialog.saveChanges')}
          </AsyncButton>
        }
      >
        <div className="space-y-4">
          <CalloutDetailsFields
            form={form}
            resourceTypes={resourceTypes}
            syncSlugFromTitle={!callout.slug}
            excludeCalloutId={callout.id}
            workspaceId={callout.enterprise_workspace_id ?? null}
            showResourceTypeInfo={false}
            services={services}
          />
        </div>
      </FormDialogContent>
    </Form>
  );
}
